import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Missionary Support-Raising Launch Checklist: What to Do Before You Go Public';
const DESCRIPTION =
    "A missionary support-raising page you launch before it's ready costs you your best moment — the first time your network sees it. The checklist to run through before you share the link with anyone.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover15.jpg',
            datePublished: '2026-08-08',
            dateModified: '2026-08-08',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/missionary-support-raising-launch-checklist',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What should I do before launching my missionary support-raising page?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Finish your vision letter, write and proofread your page bio, set a realistic monthly goal, add a real photo, and line up the first ten people you'll personally send the link to — before you post it anywhere public. The launch moment only happens once per network.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "Should I set my support goal before or after I launch my page?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Before. A goal set after launch means your first visitors see an unfinished page, and a goal changed publicly partway through support raising can look like you didn't plan carefully — set a realistic number before you share the link, even if you expect to revise it later privately.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Who should see my support-raising page first?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A small list of your closest, most likely supporters — not a public social post. Early gifts and encouraging comments from people who already know you create momentum that makes the page look active to everyone who sees it afterward.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What is the most common mistake when launching a support page?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Sharing the link publicly before the page is actually finished — a placeholder photo, an empty bio, or a goal that hasn't been set yet. Most people only look once; a rough first impression rarely gets a second look later.",
                    },
                },
            ],
        },
    ],
};

export default function MissionarySupportRaisingLaunchChecklist() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/missionary-support-raising-launch-checklist' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-launch-checklist-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover15.jpg" alt="A checklist and laptop, representing the preparation before a missionary launches a support-raising page" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>You only get one first impression with most of your network. The people most likely to give — close friends, family, your home church — will typically look at your page once, form an opinion, and either give, share, or move on. A page that's 80% finished when they see it usually gets a worse response than the same page launched a week later at 100%. Here's what to check before you share the link with anyone.</p>

                    <h2>1. Your vision letter is written and sent (or ready to send)</h2>

                    <p>Your page shouldn't be the first place someone hears about your calling. See <a href="/resources/how-to-write-a-missionary-vision-letter">how to write a missionary vision letter</a> — the personal story that gets someone to care should come before, or alongside, the link, not instead of a page that has to explain everything cold to a stranger.</p>

                    <h2>2. Your bio actually says something specific</h2>

                    <p>"We're excited to serve God overseas" tells a reader nothing they can act on. A bio that names the place, the work, and the timeline gives someone a reason to give today instead of "someday." See <a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">how to write a fundraising page bio that gets donations</a> for the specific structure that works.</p>

                    <h2>3. You have a real photo, not a placeholder</h2>

                    <p>A generic stock photo or a blank avatar is one of the fastest ways to make a support page look unfinished or, worse, untrustworthy. A real, recent photo of you — and your family, if applicable — does more for credibility in the first three seconds than any paragraph of text.</p>

                    <h2>4. Your goal is set and realistic</h2>

                    <p>Decide your monthly number before launch, not after. See <a href="/resources/how-much-support-does-a-missionary-need">how much support does a missionary need</a> if you haven't landed on a figure yet. A goal that changes visibly after people have already started giving can look like you hadn't planned carefully — set it once, thoughtfully, before anyone sees the page.</p>

                    <h2>5. You've listed your first ten people</h2>

                    <p>Don't launch by posting publicly and hoping. Write down the ten to twenty people most likely to give and support you immediately — family, close friends, your home church leadership — and plan to send them a personal message the day you go live. Early gifts and comments in the first 48 hours create visible momentum that makes the page look active to everyone who sees it next.</p>

                    <h2>6. You know what you'll say when someone asks a question</h2>

                    <p>Someone will ask where the money goes, how giving is processed, or why you chose this platform over another. Have a short, honest answer ready — see <a href="/resources/how-to-explain-platform-fees-to-your-donors">how to explain platform fees to your donors</a> if that's likely to come up — so you're not composing an answer to a trust question in real time.</p>

                    <h2>7. You have a plan for the first update</h2>

                    <p>Decide now, before launch, when your first newsletter or update will go out — two weeks later is a reasonable default. Donors who hear nothing after their first gift are the ones most likely to quietly stop giving. Planning the first update before launch means it actually happens on schedule instead of slipping for months.</p>

                    <p>Once every item above is checked, you're not launching a work in progress — you're launching something your network can respond to immediately. <a href="/">Build your page on Devotion Fund</a> and work through this list before you send the link to anyone.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What should I do before launching my missionary support-raising page?</strong><br />
                    Finish your vision letter, write your bio, set a realistic goal, add a real photo, and line up the first people you'll personally message — before sharing the link publicly.</p>

                    <p><strong>Should I set my support goal before or after I launch my page?</strong><br />
                    Before. Set a realistic number before you share the link, even if you expect to revise it later privately.</p>

                    <p><strong>Who should see my support-raising page first?</strong><br />
                    A small list of your closest, most likely supporters — not a public social post — so early gifts create visible momentum.</p>

                    <p><strong>What is the most common mistake when launching a support page?</strong><br />
                    Sharing the link before the page is actually finished. Most people only look once.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-vision-letter">How to Write a Missionary Vision Letter</a></li>
                        <li><a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">How to Write a Fundraising Page Bio That Gets Donations</a></li>
                        <li><a href="/resources/raise-support-fast-30-60-90-day-plan">Raise Support Fast: A 30-60-90 Day Plan</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this platform after watching too many missionaries launch a half-finished page out of impatience, and lose the one moment their network was paying closest attention.</p>
                    <p><small><em>Last updated: 2026-08-08.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/missionary-support-raising-launch-checklist" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
